import { z } from 'zod';

export const userProfileSchema = z.object({
  id: z.coerce.number(),
  full_name: z.string().optional(),
  tenant_id: z.coerce.number().nullish(),
});
export type UserProfile = z.infer<typeof userProfileSchema>;

export const userAccessFlagsSchema = z.object({
  is_email_notification_enabled: z.boolean(),
  is_allowed_to_generate_final_report: z.boolean(),
  is_allowed_to_create_final_report_template: z.boolean(),
  is_allowed_to_download_final_report: z.boolean(),
  is_allowed_to_download_attachments: z.boolean(),
  is_totp_enabled: z.boolean(),
  is_allowed_to_reopen_report: z.boolean(),
  is_allowed_to_request_pii_removal: z.boolean(),
  is_allowed_to_redact_report: z.boolean(),
  is_read_only_report: z.boolean(),
  is_allowed_to_send_messages: z.boolean(),
  is_allowed_to_request_for_additional_information: z.boolean(),
  language_id: z.coerce.number(),
  has_visited_user_settings: z.boolean(),
  has_visited_report_form_settings: z.boolean(),
  has_visited_program_settings: z.boolean(),
  default_property: z.coerce.number(),
});
export type UserAccessFlags = z.infer<typeof userAccessFlagsSchema>;

const reporterAccessFlags = userAccessFlagsSchema
  .pick({
    is_totp_enabled: true,
    is_email_notification_enabled: true,
    language_id: true,
  })
  .extend({ email: z.string().optional() });
export type ReporterAccessFlags = z.infer<typeof reporterAccessFlags>;

// Notification Types
const NOTIFICATION_TYPES = [
  'notification.history',
  'notification.message',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);

export const recipientSchema = z.object({
  id: z.number(),
  is_read: z.boolean(),
  user: z.object({
    id: z.number(),
    full_name: z.string().nullable(),
  }),
});
export type Recipient = z.infer<typeof recipientSchema>;

export const attendeeSchema = z.object({
  attendee: z.object({
    id: z.number(),
    full_name: z.string().nullable(),
    user_type: z.number(),
  }),
  confirm: z.number(),
});
export type Attendee = z.infer<typeof attendeeSchema>;

export const notificationRecordSchema = z.object({
  id: z.number(),
  created_by: z.object({
    id: z.number(),
    full_name: z.string().nullable(),
    user_type: z.number(),
  }),
  link: z.string(),
  date_and_time: z.string().datetime(),
  type: z.number(),
  report: z.object({
    id: z.string(),
    report_type: z.object({
      id: z.number(),
      name: z.string(),
      type: z.number(),
    }),
    country_verbose: z.object({
      code: z.string(),
      name: z.string(),
    }),
  }),
  created_at: z.string().datetime(),
  attendees: z.array(attendeeSchema),
  case_handler: z.record(z.any()).nullable(),
});

export type NotificationRecord = z.infer<typeof notificationRecordSchema>;

export const notificationSchema = z.object({
  content: z.string(),
  model_type: z.string(),
  record_obj: notificationRecordSchema,
  created_at: z.string().datetime(),
  recipients: z.array(recipientSchema),
});
export type NotificationItem = z.infer<typeof notificationSchema>;

export const notificationHistorySchema = z.object({
  type: notificationTypeSchema,
  user_id: z.number(),
  notifications: z.array(notificationSchema),
});

export type NotificationHistory = z.infer<typeof notificationHistorySchema>;

export const newNotificationSchema = z.object({
  type: notificationTypeSchema,
  action: z.string(),
  content: z.string(),
  model_type: z.string(),
  record_obj: notificationRecordSchema,
  created_at: z.string().datetime(),
  recipients: z.array(recipientSchema),
});
export type NewNotification = z.infer<typeof newNotificationSchema>;

export const reportReferenceIdSchema = z.object({
  reportReferenceId: z
    .string()
    .regex(/^[0-9]*$/i, {
      message: 'Invalid Report ID',
    })
    .max(36)
    .or(z.string().length(0)),
});
